// The i18next instance and the language lifecycle (Phase 3 localization).
//
// English is BUNDLED and initialized synchronously at import, so `t()` returns
// English on the first call with no provider, no Suspense and no await. That
// is what lets the tests (src/test/setup.ts imports this module) and the
// Node prerender (src/prerender/checker-entry.tsx) render without waiting.
//
// Spanish, Portuguese and Japanese are lazy chunks (src/locales/<lang>/_bundle.ts),
// loaded by initLanguage() before the first render and ONLY when the
// VITE_I18N build flag is on. Flag off, this module never reads
// localStorage or navigator and never requests a chunk, and the chunks are
// not even in the build.
//
// Nothing here touches a browser global at module scope: the prerender loads
// this file in Node (pinned by src/__tests__/prerenderGraph.test.ts).
import { useSyncExternalStore } from 'react'
import i18next, { type i18n as I18n, type Resource } from 'i18next'
import { initReactI18next } from 'react-i18next'
import { resolveLanguage } from './detect'
import { i18nEnabled } from './flag'
import { DEFAULT_LANG, LANGUAGE_STORAGE_KEY, isLang, type Lang } from './languages'
import enCommon from '../locales/en/common.json'
import enPro from '../locales/en/pro.json'

export const NAMESPACES = ['common', 'bracket', 'build', 'decks', 'collection', 'pro', 'watchtower', 'site'] as const
export type Namespace = (typeof NAMESPACES)[number]

// English namespaces that ship with the ROUTE or FEATURE chunks that use
// them, not in this module's chunk (which every page preloads). Each one has
// a small registration module under src/i18n/ns/ that imports its JSON and
// calls registerEnglishNamespace() when it evaluates, so the catalog is in
// place before any component of that area renders: no flash, no Suspense,
// and still synchronous for tests and the prerender (the component's own
// import graph carries it). A module that looks one of these namespaces up
// must import its registration module; src/i18n/__tests__/namespaceImports
// .test.ts enforces that, and scripts/check-i18n-bundle.mjs proves the
// strings are not in the shared chunk.
export const ROUTE_NAMESPACES = ['bracket', 'build', 'decks', 'collection', 'watchtower', 'site'] as const satisfies readonly Namespace[]
type RouteNamespace = (typeof ROUTE_NAMESPACES)[number]

/**
 * The English catalog of every namespace, as registered so far. The bundled
 * namespaces are filled at import; a route namespace is `{}` until its
 * registration module runs (codeLabel/hasCode then return null, and the
 * caller keeps its raw-code fallback, so nothing renders a key path).
 */
export const EN_RESOURCES: Record<Namespace, Record<string, unknown>> = {
  common: enCommon,
  bracket: {},
  build: {},
  decks: {},
  collection: {},
  pro: enPro,
  watchtower: {},
  site: {},
}

const BUNDLED_EN = Object.fromEntries(
  NAMESPACES.filter(ns => !(ROUTE_NAMESPACES as readonly string[]).includes(ns)).map(ns => [ns, EN_RESOURCES[ns]]),
)

const i18n: I18n = i18next.createInstance()

/**
 * Initialize the instance with the bundled English catalogs, synchronously.
 * Idempotent: the module calls it at import, and the prerender entry calls it
 * again explicitly so its intent is visible there.
 */
export function initEnglishSync(): I18n {
  if (i18n.isInitialized) return i18n
  i18n.use(initReactI18next)
  void i18n.init({
    // Resources are inline, so with initAsync false init() finishes before it
    // returns and t() is usable on the next line.
    initAsync: false,
    // Only the bundled namespaces; route namespaces arrive through
    // registerEnglishNamespace().
    resources: { en: BUNDLED_EN } as Resource,
    lng: DEFAULT_LANG,
    fallbackLng: DEFAULT_LANG,
    ns: [...NAMESPACES],
    // eslint-disable-next-line i18next/no-literal-string -- i18next event or namespace name
    defaultNS: 'common',
    // React escapes text itself; escaping here would double-escape.
    interpolation: { escapeValue: false },
    returnNull: false,
    react: { useSuspense: false },
  })
  // eslint-disable-next-line i18next/no-literal-string -- i18next event or namespace name
  i18n.on('languageChanged', syncHtmlLang)
  return i18n
}

/**
 * Register a route namespace's English catalog. Called once, at module
 * evaluation, by its registration module (src/i18n/ns/<ns>.ts). Idempotent.
 * Synchronous, so the first render of the area that imported it already has
 * its English.
 */
export function registerEnglishNamespace(ns: RouteNamespace, resources: Record<string, unknown>): void {
  if (EN_RESOURCES[ns] === resources) return
  EN_RESOURCES[ns] = resources
  i18n.addResourceBundle(DEFAULT_LANG, ns, resources, true, true)
}

/**
 * Keep `<html lang>` on the active interface language, so screen readers
 * pronounce it right and browsers stop offering to translate Spanish that is
 * already Spanish. Only with the flag on: off, index.html's `lang="en"` is
 * never touched. A non-interface language (the test-only `pseudo`) is ignored.
 */
function syncHtmlLang(lang: string): void {
  if (!i18nEnabled() || !isLang(lang)) return
  if (typeof document === 'undefined') return
  document.documentElement.lang = lang
}

initEnglishSync()

/**
 * `t` bound to the bundled `common` namespace (the app shell: header, footer,
 * sign-in, home, shared errors), for module-level helpers. Reads the language
 * at CALL time, like the route namespaces' bound `t`s in src/i18n/ns/. The
 * literal guard exempts its arguments: they are key paths.
 */
// eslint-disable-next-line i18next/no-literal-string -- the namespace name
export const tc = i18n.getFixedT(null, 'common')

/**
 * The shell components' hook: `const t = useCommonT()`. Same `t` as `tc`,
 * plus a re-render when the language changes (useLanguageTick, below; not
 * react-i18next's useTranslation, for the chunk reason given there).
 */
export function useCommonT(): typeof tc {
  useLanguageTick()
  return tc
}

// The lazy catalog chunks. One function per language so Rolldown emits one
// chunk each, and each is loaded only for the language actually chosen.
//
// Guarded by the raw `__I18N__` define, NOT i18nEnabled(): only a literal
// `false` right here drops the locale chunks from a flag-off build. Measured
// with Rolldown: guarding with i18nEnabled(), or with a const exported from
// flag.ts, still emitted both chunks. scripts/check-i18n-bundle.mjs checks
// the manifest. Keep this a direct ternary with nothing in between. This is
// the only reader of `__I18N__` outside flag.ts.
type LocaleBundle = { LOCALE_SENTINEL: string; resources: Record<string, unknown> }
const LOADERS: Record<Exclude<Lang, 'en'>, () => Promise<LocaleBundle>> | null = __I18N__
  ? {
      es: () => import('../locales/es/_bundle'),
      pt: () => import('../locales/pt/_bundle'),
      ja: () => import('../locales/ja/_bundle'),
    }
  : null

// Sentinels of the chunks this page has loaded, in load order. Reading the
// sentinel here is also what keeps it in the emitted chunk for
// scripts/check-i18n-bundle.mjs.
const loadedSentinels: string[] = []

/** Test seam: which locale chunks have been loaded on this page. */
export function loadedLocaleSentinels(): readonly string[] {
  return loadedSentinels
}

// Loads a language's catalog chunk and registers it. It does NOT switch
// language: the only switch is in initLanguage(), which bootstrap runs once
// before the first render (see "Changing the language" below).
async function loadLanguage(lang: Lang): Promise<void> {
  if (lang === 'en' || !i18nEnabled() || !LOADERS) return
  // eslint-disable-next-line i18next/no-literal-string -- i18next event or namespace name
  if (!i18n.hasResourceBundle(lang, 'common')) {
    const bundle = await LOADERS[lang]()
    loadedSentinels.push(bundle.LOCALE_SENTINEL)
    for (const ns of NAMESPACES) {
      // An empty `{}` namespace (not translated yet) is still registered, so
      // every key falls back to English per key via fallbackLng.
      i18n.addResourceBundle(lang, ns, bundle.resources[ns] ?? {}, true, true)
    }
  }
}

function readStoredChoice(): string | null {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY)
  } catch {
    return null
  }
}

function readNavigatorLanguages(): readonly string[] {
  try {
    if (typeof navigator === 'undefined') return []
    if (Array.isArray(navigator.languages) && navigator.languages.length) return navigator.languages
    return navigator.language ? [navigator.language] : []
  } catch {
    return []
  }
}

/**
 * Pick and load the interface language. Runs once in bootstrap(), BEFORE
 * createRoot renders, so the first painted frame is already in the chosen
 * language.
 *
 * Never rejects: a failed chunk load logs, stays on English and resolves.
 * Flag off it returns 'en' immediately, without reading localStorage or
 * navigator and without requesting any chunk.
 */
export async function initLanguage(): Promise<Lang> {
  if (!i18nEnabled()) return DEFAULT_LANG
  let lang: Lang = DEFAULT_LANG
  try {
    lang = resolveLanguage({
      enabled: true,
      stored: readStoredChoice(),
      navigatorLanguages: readNavigatorLanguages(),
    })
    if (lang !== DEFAULT_LANG) await loadLanguage(lang)
    // THE one place the interface language changes (see "Changing the
    // language" below). Before the first render in the browser; tests call
    // initLanguage() again to stand in for the reload.
    if (i18n.language !== lang) await i18n.changeLanguage(lang)
  } catch (err) {
    console.warn('[i18n] could not load the interface language, staying on English', err)
    lang = DEFAULT_LANG
    try {
      await i18n.changeLanguage(DEFAULT_LANG)
    } catch {
      // English is bundled; there is nothing left to fail.
    }
  }
  syncHtmlLang(lang)
  return lang
}

/** The language the interface is rendering in right now. */
export function activeLanguage(): Lang {
  const lang = i18n.language
  return isLang(lang) ? lang : DEFAULT_LANG
}

function subscribeLanguage(onChange: () => void): () => void {
  // eslint-disable-next-line i18next/no-literal-string -- i18next event or namespace name
  i18n.on('languageChanged', onChange)
  // eslint-disable-next-line i18next/no-literal-string -- i18next event or namespace name
  return () => i18n.off('languageChanged', onChange)
}

function currentLanguage(): string {
  return i18n.language
}

/**
 * Re-render the calling component when the interface language changes, and
 * return that language. The route namespaces' hooks (src/i18n/ns/*) are
 * built on this instead of react-i18next's useTranslation: with many lazy
 * chunks importing useTranslation, Rolldown split it out of the entry into a
 * shared chunk of its own, which added a modulepreload to every prerendered
 * page (3d). The snapshot is a plain string, so the Node prerender reads it
 * through the server-snapshot argument.
 */
export function useLanguageTick(): string {
  return useSyncExternalStore(subscribeLanguage, currentLanguage, currentLanguage)
}

// The account-side copy of the choice. lib/supabase reads browser globals at
// module scope and this module is in the prerender graph, so the Supabase
// write is REGISTERED from hooks/useAuth.ts instead of imported here.
// Ref-counted because every mounted useAuth() registers the same function.
type LanguagePersister = (lang: Lang) => Promise<void>
const persisters = new Map<LanguagePersister, number>()

/**
 * Register a best-effort writer of the chosen language to the signed-in
 * account. Returns the matching unregister.
 */
export function registerLanguagePersister(fn: LanguagePersister): () => void {
  persisters.set(fn, (persisters.get(fn) ?? 0) + 1)
  return () => {
    const n = (persisters.get(fn) ?? 0) - 1
    if (n > 0) persisters.set(fn, n)
    else persisters.delete(fn)
  }
}

// ---------------------------------------------------------------------------
// Changing the language (Keith's decision: a language change RELOADS).
//
// i18n.changeLanguage runs only inside initLanguage(), which bootstrap awaits
// before createRoot renders. Nothing switches the language of a mounted tree:
// text already on screen (catalog strings memoized in components, and the
// API's prose) would otherwise stay in the old language. A change of choice
// is stored, then the page reloads and initLanguage() applies it.
// src/i18n/__tests__/languageChangeSites.test.ts pins this.
// ---------------------------------------------------------------------------

function storeChoice(lang: Lang): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang)
  } catch {
    // Private mode or a full quota: the reload then comes back on the
    // detected language, which is the best this browser can do.
  }
}

/**
 * Record the user's interface language: stored FIRST (so the reload that
 * follows comes back in it), then written to the signed-in account, best
 * effort. It does not change the language of the page: the caller (the
 * switcher) reloads next. Never rejects. A no-op with the flag off.
 */
export async function setLanguage(lang: Lang): Promise<void> {
  if (!i18nEnabled() || !isLang(lang)) return
  storeChoice(lang)
  for (const persist of [...persisters.keys()]) {
    try {
      await persist(lang)
    } catch (err) {
      console.warn('[i18n] could not save the language to the account', err)
    }
  }
}

// One reload per tab session for an adopted account language, whatever
// happens: if the store refused the write, the reload would come back on the
// old language and adopt again, forever.
const ADOPT_RELOAD_GUARD_KEY = 'cb_interface_language_adopt_reload'

function reloadPage(): void {
  window.location.reload()
}

/**
 * Adopt the language stored on the account after sign-in (hooks/useAuth.ts).
 * When it differs from the page's language: store it, then reload ONCE so
 * initLanguage() applies it to a fresh tree. After the reload the stored
 * choice equals the account's, so this returns early; a sessionStorage
 * one-shot guard stops a loop even if storage refused the write. Never
 * writes back to the account it just read. Returns whether it reloaded. A
 * no-op with the flag off or for a value that is not an interface language.
 */
export function adoptAccountLanguage(lang: unknown): boolean {
  if (!i18nEnabled() || !isLang(lang) || lang === activeLanguage()) return false
  try {
    if (sessionStorage.getItem(ADOPT_RELOAD_GUARD_KEY) === lang) return false
    sessionStorage.setItem(ADOPT_RELOAD_GUARD_KEY, lang)
  } catch {
    // No sessionStorage: no guard to trust, so do not risk a loop.
    return false
  }
  storeChoice(lang)
  reloadPage()
  return true
}

// ---------------------------------------------------------------------------
// Catalog labels for closed code families (Phase 3, step 3b.2).
//
// Kept in this module, not a separate one: a separate module became its own
// shared chunk and added a modulepreload hint to every prerendered page.
// ---------------------------------------------------------------------------
//
// Many API values are codes the web renders as text: an engine code, a swap
// category, a quota bucket. Each family is a nested object in a namespace
// (`bracket:engine.<code>`, `pro:swap_category.<code>`), and the code stays
// the identity for every comparison; only the label is translated.
//
// Membership is decided against the BUNDLED ENGLISH catalog, never by asking
// i18next, so:
// - an unknown code returns null and the caller keeps today's fallback;
// - a code that is not a safe key ("a.b", "x:y", "__proto__") never reaches
//   i18next as a key path;
// - the answer is the same in every language (es/pt/ja are subsets of en).

/** The English object at `family` (a key path like "facet.finish"), or null. */
function familyTable(ns: Namespace, family: string): Record<string, unknown> | null {
  let node: unknown = EN_RESOURCES[ns]
  for (const segment of family.split('.')) {
    if (!node || typeof node !== 'object' || !Object.hasOwn(node, segment)) return null
    node = (node as Record<string, unknown>)[segment]
  }
  return node && typeof node === 'object' ? (node as Record<string, unknown>) : null
}

/** True when `<ns>:<family>.<code>` is a catalog key. */
export function hasCode(ns: Namespace, family: string, code: string): boolean {
  const table = familyTable(ns, family)
  return !!table && Object.hasOwn(table, code) && typeof table[code] === 'string'
}

/** The label for a code in the active language, or null for an unknown code. */
export function codeLabel(
  ns: Namespace,
  family: string,
  code: string,
  vars?: Record<string, string | number>,
): string | null {
  if (!hasCode(ns, family, code)) return null
  return i18n.t(`${family}.${code}`, { ns, ...vars })
}

/**
 * The label for an API value whose ENGLISH text comes from the API itself
 * (an option list, a closed set of server phrases), keyed in the catalog by
 * its identity code.
 *
 * - English: the API's label, verbatim, so a server-side relabel shows up as
 *   sent; the web catalog's English is only the translation source.
 * - Any other language: the translation in the ACTIVE language's resource
 *   only. There is no English fallback here: a code the active catalog does
 *   not translate yet renders the API's label, never the web catalog's
 *   (possibly stale) English. This is what codeLabel() cannot do, because it
 *   goes through i18next's fallbackLng.
 * - No API label at all: `legacy`, the rendering the caller had before.
 *
 * Arguments: (ns, family, code) as for codeLabel, checked the same way by
 * i18n-local/catalog-key-args; `api` and `legacy` are runtime values (the
 * rule rejects written text there).
 */
export function apiLabel(
  ns: Namespace,
  family: string,
  code: string,
  api: string | null | undefined,
  legacy: string = code,
): string {
  const fromApi = typeof api === 'string' && api !== '' ? api : null
  const lang = i18n.language
  if (lang === DEFAULT_LANG || !hasCode(ns, family, code)) return fromApi ?? legacy
  const translated = i18n.getResource(lang, ns, `${family}.${code}`)
  if (typeof translated === 'string' && translated !== '') return translated
  return fromApi ?? legacy
}

// The Intl formatters (src/i18n/format.ts), re-exported so app code imports
// them from here. Imported directly from many route chunks, format.ts became
// a chunk of its own and added a modulepreload hint to 15 prerendered pages;
// reached only through this module, it rides in this module's chunk. The
// cycle is safe: format.ts reads activeLanguage() at call time only.
// src/i18n/__tests__/formatImports.test.ts keeps app code on this path.
export * from './format'

export default i18n
